import { useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Paperclip, ExternalLink, Trash2, Upload, Cloud } from 'lucide-react'
import type { OneDriveFile } from '@/types'
import { api } from '@/lib/api'
import { formatFileSize } from '@/lib/utils'

interface Props {
  itemId: string
  onPickOneDrive: (onSave: (file: OneDriveFile & { driveId: string }) => Promise<void>) => void
}

export default function AttachmentList({ itemId, onPickOneDrive }: Props) {
  const qc = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)

  const { data: attachments = [] } = useQuery({
    queryKey: ['attachments', itemId],
    queryFn: () => api.attachments.list(itemId),
  })

  const uploadMutation = useMutation({
    mutationFn: (file: File) => api.attachments.upload(itemId, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attachments', itemId] }),
  })

  const linkMutation = useMutation({
    mutationFn: (file: OneDriveFile & { driveId: string }) =>
      api.attachments.linkOneDrive(itemId, {
        id: file.id,
        driveId: file.driveId,
        name: file.name,
        webUrl: file.webUrl,
        size: file.size,
        mimeType: file.mimeType,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attachments', itemId] }),
  })

  const deleteMutation = useMutation({
    mutationFn: (attachmentId: string) => api.attachments.delete(itemId, attachmentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attachments', itemId] }),
  })

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
          <Paperclip size={14} /> Documents
        </h3>
        <div className="flex gap-2">
          <button
            onClick={() => fileRef.current?.click()}
            className="text-xs text-brand-600 hover:underline flex items-center gap-1"
          >
            <Upload size={12} /> Upload
          </button>
          <button
            onClick={() => onPickOneDrive(async (file) => { await linkMutation.mutateAsync(file) })}
            className="text-xs text-brand-600 hover:underline flex items-center gap-1"
          >
            <Cloud size={12} /> OneDrive
          </button>
        </div>
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          onChange={e => {
            const file = e.target.files?.[0]
            if (file) uploadMutation.mutate(file)
          }}
        />
      </div>

      {attachments.length === 0 ? (
        <p className="text-xs text-slate-400 py-2">No documents attached yet.</p>
      ) : (
        <ul className="space-y-1.5">
          {attachments.map(att => (
            <li key={att.id} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2">
              <Paperclip size={14} className="text-slate-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-800 truncate">{att.name}</p>
                {att.size_bytes && <p className="text-xs text-slate-400">{formatFileSize(att.size_bytes)}</p>}
              </div>
              {att.source === 'onedrive' && att.onedrive_web_url ? (
                <a href={att.onedrive_web_url} target="_blank" rel="noreferrer" className="p-1 text-slate-400 hover:text-brand-600">
                  <ExternalLink size={14} />
                </a>
              ) : (
                <a href={api.attachments.downloadUrl(itemId, att.id)} target="_blank" rel="noreferrer" className="p-1 text-slate-400 hover:text-brand-600">
                  <ExternalLink size={14} />
                </a>
              )}
              <button
                onClick={() => { if (confirm('Remove this attachment?')) deleteMutation.mutate(att.id) }}
                className="p-1 text-slate-400 hover:text-red-500"
              >
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
